import { GlAvatar, GlButton, GlButtonGroup, GlLabel, GlTooltip, GlIcon, GlSprintf, GlTooltipDirective, GlAnimatedChevronLgRightDownIcon } from '../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { parseBoolean } from '@gitlab/frontend-utils';
import { getBoardListTitleId, isListDraggable } from '~/boards/boards_util';
import { isScopedLabel } from '~/lib/utils/common_utils';
import { fetchPolicies } from '../../lib/graphql.js?vue3';
import { BV_HIDE_TOOLTIP } from '~/lib/utils/constants';
import { n__, s__ } from '~/locale';
import Tracking from '../../tracking/index.js?vue3';
import { TYPE_ISSUE } from '~/issues/constants';
import setActiveBoardItemMutation from 'ee_else_ce/boards/graphql/client/set_active_board_item.mutation.graphql';
import MilestonePopover from '../../issuable/popover/components/milestone_popover.vue?vue3';
import AccessorUtilities from '~/lib/utils/accessor';
import { ListType, updateListQueries, toggleCollapsedMutations, listsDeferredQuery } from 'ee_else_ce/boards/constants';
import { setError } from '../graphql/cache_updates.js?vue3';
import ItemCount from './item_count.vue?vue3';
export default {
    name: 'BoardListHeader',
    i18n: {
        newIssue: s__('Boards|Create new issue'),
        newEpic: s__('Boards|Create new epic'),
        listSettings: s__('Boards|Edit list settings'),
        expand: s__('Boards|Expand'),
        collapse: s__('Boards|Collapse'),
        fetchError: s__("Boards|An error occurred while fetching list's information. Please try again.")
    },
    components: {
        GlAvatar,
        GlButton,
        GlButtonGroup,
        GlLabel,
        GlTooltip,
        MilestonePopover,
        GlIcon,
        GlSprintf,
        GlAnimatedChevronLgRightDownIcon,
        ItemCount
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        Tracking.mixin()
    ],
    inject: {
        weightFeatureAvailable: {
            default: false
        },
        scopedLabelsAvailable: {
            default: false
        },
        currentUserId: {
            default: null
        },
        canCreateEpic: {
            default: false
        },
        isEpicBoard: {
            default: false
        },
        disabled: {
            default: true
        },
        issuableType: {
            default: TYPE_ISSUE
        }
    },
    props: {
        list: {
            type: Object,
            default: ()=>({}),
            required: false
        },
        isSwimlanesHeader: {
            type: Boolean,
            required: false,
            default: false
        },
        filterParams: {
            type: Object,
            required: true
        },
        boardId: {
            type: String,
            required: true
        }
    },
    emits: [
        'open-unassigned-lane',
        'set-active-list',
        'toggle-new-form'
    ],
    data () {
        return {
            boardList: undefined,
            showMilestonePopover: false
        };
    },
    computed: {
        isLoggedIn () {
            return Boolean(this.currentUserId);
        },
        listType () {
            return this.list.listType;
        },
        isLabelList () {
            return this.listType === ListType.label;
        },
        itemsCount () {
            return this.isEpicBoard ? this.list.metadata.epicsCount : this.boardList?.issuesCount;
        },
        hasItemsCount () {
            return typeof this.itemsCount === 'number';
        },
        boardItemsSizeExceedsMax () {
            return this.list.maxIssueCount > 0 && this.itemsCount > this.list.maxIssueCount;
        },
        listAssignee () {
            return this.list?.assignee?.username || '';
        },
        listTitle () {
            return this.list?.label?.description || this.list?.assignee?.name || this.list.title || '';
        },
        listTitleId () {
            return getBoardListTitleId(this.list.id);
        },
        listStatus () {
            return this.list?.status || {};
        },
        listStatusColor () {
            return this.listStatus?.color;
        },
        listStatusIconName () {
            return this.listStatus?.iconName;
        },
        showListHeaderButton () {
            return !this.disabled && this.listType !== ListType.closed;
        },
        showAssigneeListDetails () {
            return this.listType === ListType.assignee && this.showListDetails;
        },
        showListDetails () {
            return !this.list.collapsed || !this.isSwimlanesHeader;
        },
        showListHeaderActions () {
            if (this.isLoggedIn) {
                return (this.isNewIssueShown || this.isNewEpicShown || this.isSettingsShown) && !this.list.collapsed;
            }
            return false;
        },
        countIcon () {
            return 'issues';
        },
        itemsTooltipLabel () {
            return n__(`%d issue`, `%d issues`, this.itemsCount);
        },
        chevronTooltip () {
            return this.list.collapsed ? this.$options.i18n.expand : this.$options.i18n.collapse;
        },
        isNewIssueShown () {
            return (this.listType === ListType.backlog || this.showListHeaderButton) && !this.isEpicBoard;
        },
        isNewEpicShown () {
            return this.isEpicBoard && this.canCreateEpic && this.listType !== ListType.closed;
        },
        isSettingsShown () {
            return this.listType !== ListType.backlog && this.listType !== ListType.closed && !this.list.collapsed;
        },
        uniqueKey () {
            // eslint-disable-next-line @gitlab/require-i18n-strings
            return `boards.${this.boardId}.${this.listType}.${this.list.id}`;
        },
        collapsedTooltipTitle () {
            return this.listTitle || this.listAssignee;
        },
        headerStyle () {
            return {
                borderTopColor: this.list?.label?.color
            };
        },
        userCanDrag () {
            return !this.disabled && isListDraggable(this.list);
        },
        // due to the issues with cache-and-network, we need this hack to check if there is any data for the query in the cache.
        // if we have cached data, we disregard the loading state
        isLoading () {
            return this.$apollo.queries.boardList.loading && !this.$apollo.provider.clients.defaultClient.readQuery({
                query: listsDeferredQuery[this.issuableType].query,
                variables: this.countQueryVariables
            });
        },
        totalIssueWeight () {
            return parseInt(this.boardList?.totalIssueWeight, 10);
        },
        canShowTotalWeight () {
            return this.weightFeatureAvailable && !this.isLoading;
        },
        countQueryVariables () {
            return {
                id: this.list.id,
                filters: this.filterParams
            };
        },
        showStatusIcon () {
            return this.listType === 'status' && (!this.isSwimlanesHeader || !this.list.collapsed);
        }
    },
    apollo: {
        boardList: {
            fetchPolicy: fetchPolicies.CACHE_AND_NETWORK,
            query () {
                return listsDeferredQuery[this.issuableType].query;
            },
            variables () {
                return this.countQueryVariables;
            },
            error (error) {
                setError({
                    error,
                    message: this.$options.i18n.fetchError
                });
            }
        }
    },
    created () {
        const localCollapsed = parseBoolean(localStorage.getItem(`${this.uniqueKey}.collapsed`));
        if ((!this.isLoggedIn || this.isEpicBoard) && localCollapsed) {
            this.updateLocalCollapsedStatus(true);
        }
    },
    methods: {
        openSidebarSettings () {
            this.$apollo.mutate({
                mutation: setActiveBoardItemMutation,
                variables: {
                    boardItem: null,
                    listId: null
                }
            });
            this.$emit('set-active-list', this.list.id);
            this.track('click_button', {
                label: 'list_settings'
            });
        },
        showScopedLabels (label) {
            return this.scopedLabelsAvailable && isScopedLabel(label);
        },
        showNewForm () {
            if (this.isSwimlanesHeader) {
                this.$emit('open-unassigned-lane');
                this.$nextTick(()=>{
                    this.$emit('toggle-new-form');
                });
            } else {
                this.$emit('toggle-new-form');
            }
        },
        toggleExpanded () {
            const collapsed = !this.list.collapsed;
            this.updateLocalCollapsedStatus(collapsed);
            if (!this.isLoggedIn) {
                this.addToLocalStorage(collapsed);
            } else {
                this.updateListFunction(collapsed);
            }
            // When expanding/collapsing, the tooltip on the caret button sometimes stays open.
            // Close all tooltips manually to prevent dangling tooltips.
            this.$root.$emit(BV_HIDE_TOOLTIP);
            this.track('click_toggle_button', {
                label: 'toggle_list',
                property: collapsed ? 'closed' : 'open'
            });
        },
        addToLocalStorage (collapsed) {
            if (AccessorUtilities.canUseLocalStorage()) {
                localStorage.setItem(`${this.uniqueKey}.collapsed`, collapsed);
            }
        },
        async updateListFunction (collapsed) {
            try {
                await this.$apollo.mutate({
                    mutation: updateListQueries[this.issuableType].mutation,
                    variables: {
                        listId: this.list.id,
                        collapsed
                    },
                    optimisticResponse: {
                        updateBoardList: {
                            __typename: 'UpdateBoardListPayload',
                            errors: [],
                            list: {
                                ...this.list,
                                collapsed
                            }
                        }
                    }
                });
            } catch (error) {
                setError({
                    error,
                    message: s__('Boards|An error occurred while updating the list. Please try again.')
                });
            }
        },
        updateLocalCollapsedStatus (collapsed) {
            this.$apollo.mutate({
                mutation: toggleCollapsedMutations[this.issuableType].mutation,
                variables: {
                    list: this.list,
                    collapsed
                }
            });
        }
    }
};
